<template>
    <data-by-unit-chart
        :data="processChartData(climateData)"
        :provided-options="{ ...options }"
        :height="70"
        class="monthly-climate-sparkline-chart"
    />
</template>
<script>
import DataByUnitChart from '@foundryspatial/data-by-unit-chart';

export default {
    components: {
        DataByUnitChart,
    },

    props: {
        // array of objects containing data for each station
        data: {
            type: Array,
            required: true,
        },

        // array of data points for single station and climate type
        climateData: {
            type: Array,
            required: true,
        },

        lineColor: {
            type: String,
            required: true,
        },

        unitOfMeasurement: {
            type: String,
            required: false,
            default: '',
        },

        valueName: {
            type: String,
            required: false,
            default: 'Value',
        },

        climateType: {
            type: String,
            required: true,
        },
    },

    computed: {
        options() {
            const { domainMax, domainMin } = this.fixedDomains;
            return {
                unitOfMeasurement: this.unitOfMeasurement,
                roundToDecimalPlaces: 2,
                valueScaleOptions: {
                    domainMax,
                    domainMin,
                },

                valueAxisOptions: {
                    tickCount: 2,
                },

                timeAxisOptions: {
                    labelOverlap: false,
                    labelFontSize: 7,
                    labelFontWeight: 50,
                    encode: {
                        labels: {
                            update: {
                                // substring is used to only display the first character of the datum value
                                text: { signal: 'substring(datum.label, 0, 1)' },
                            },
                        },
                    },
                },

                tooltipOnWholeChart: true,
            };
        },

        /**
         * Configures the fixed max and min domain to have a standardized domain across the different climate charts.
         * The max and min values are then multiplied by 1.15 to add a buffer so the line doesn't reach the very
         * top/bottom of the graph.
         *
         * @returns {object} - containing fixed min and max domain values
         */
        fixedDomains() {
            const largestValues = this.data.map((data) => Math.max(...data[this.climateType], 0));
            const max = Math.max(...largestValues, 0) * 1.15;

            const smallestValues = this.data.map((data) => Math.min(...data[this.climateType]));
            let min = Math.min(...smallestValues);
            min = min >= 0 ? 0 : min * 1.15;

            return ({
                domainMax: max,
                domainMin: min,
            });
        },
    },

    methods: {
        /**
         * Processes the data into the proper format required by the chart component
         *
         * @param {Array} data - contains monthly chart values
         * @returns {Array} - array of objects containing tooltip name, color, and data for each month Jan-Dec
         */
        processChartData(data) {
            const formattedData = [];
            data.forEach((value, index) => {
                formattedData.push({
                    month: index + 1,
                    value,
                });
            });

            return [
                {
                    unit: 'month',
                    unitKey: 'month',
                    marks: [
                        {
                            type: 'line',
                            color: this.lineColor,
                            value: 'value',
                            valueName: this.valueName,
                            labelOrder: 1,
                        },
                    ],

                    data: formattedData,
                },
            ];
        },
    },
};
</script>
<style lang="scss">
.monthly-climate-sparkline-chart {
    height: 100%;
    width: 100%;
}
</style>
